// DispatchDeck — calendar date for safety records (TASK-9923)
// ============================================================================
// `window.RecordDate` — "what day is it" for DVIR / pre-trip / JSA records,
// answered on the COMPANY's calendar, never UTC.
//
// The bug this exists to stop: `new Date().toISOString().split('T')[0]` is the
// UTC date. After ~7 PM Central that is TOMORROW, so a pre-trip filed at 8 PM
// was stamped the next day — a DOT record with the wrong date.
//
// Zone order: tenant settings.timezone (what Settings writes) → tenant-root
// timezone → the device's own zone → device-local Date getters. An unknown
// zone string is skipped, never thrown on.
//
// Pure, no imports, no DOM: loaded as a side-effect import and unit-tested in
// node (tests/task9923-record-date.unit.cjs runs this file in a vm with a stub
// `window`). Intl does all zone math, so DST follows the tz database.
// ============================================================================
(function (root) {
  'use strict';

  const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
  const pad = (n) => String(n).padStart(2, '0');

  const _ok = new Map();
  function validZone(tz) {
    const z = typeof tz === 'string' ? tz.trim() : '';
    if (!z) return '';
    if (_ok.has(z)) return _ok.get(z);
    let out = '';
    try { new Intl.DateTimeFormat('en-US', { timeZone: z }).format(new Date(0)); out = z; } catch (_) { out = ''; }
    if (_ok.size < 64) _ok.set(z, out);
    return out;
  }

  /** The company's zone. `src` = { settings, tenantData } (defaults to window.AppState). '' = device-local. */
  function zone(src) {
    const s = src || root.AppState || {};
    const settings = s.settings || (s.tenantData && s.tenantData.settings) || {};
    const fromTenant = validZone(settings.timezone) || validZone(s.tenantData && s.tenantData.timezone);
    if (fromTenant) return fromTenant;
    let device = '';
    try { device = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { device = ''; }
    return validZone(device);
  }

  /** Wall-clock parts of an instant. tz '' = device-local. month is 1-12. */
  function parts(ms, tz) {
    const z = validZone(tz);
    const d = new Date(ms);
    if (!z) {
      return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds() };
    }
    const out = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: z, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(d).forEach((p) => { if (p.type !== 'literal') out[p.type] = Number(p.value); });
    return { year: out.year, month: out.month, day: out.day, hour: out.hour % 24, minute: out.minute, second: out.second };
  }

  /** 'YYYY-MM-DD' of an instant on the company calendar. */
  function ymd(ms, tz) {
    const t = ms instanceof Date ? ms.getTime() : Number(ms);
    if (!Number.isFinite(t)) return '';
    const p = parts(t, tz);
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
  }

  /** Today on the company calendar. */
  function today(nowMs, tz) {
    return ymd(Number.isFinite(nowMs) ? nowMs : Date.now(), tz === undefined ? zone() : tz);
  }

  /** 'YYYY-MM-DD' + n calendar days ('' on a bad date). */
  function addDays(s, n) {
    const m = YMD_RE.exec(String(s || ''));
    if (!m || !Number.isInteger(n)) return '';
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  }

  /**
   * Company wall time -> instant (ms). tz '' = device-local. Same DST policy as
   * utils/reminderTime.js wallToUtcMs: a wall time in the spring-forward gap
   * moves FORWARD past the gap; a fall-back repeat resolves to the first one.
   */
  function wallToMs(y, mo, d, h, mi, sec, tz) {
    const z = validZone(tz);
    if (!z) return new Date(y, mo - 1, d, h, mi, sec).getTime();
    const guess = Date.UTC(y, mo - 1, d, h, mi, sec);
    if (!Number.isFinite(guess)) return NaN;
    const off = (ms) => { const b = Math.floor(ms / 1000) * 1000; const p = parts(b, z); return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - b; };
    const hit = (ms) => { const p = parts(ms, z); return p.year === y && p.month === mo && p.day === d && p.hour === h && p.minute === mi; };
    const cA = guess - off(guess - 864e5);
    const cB = guess - off(guess + 864e5);
    const okA = hit(cA);
    const okB = hit(cB);
    if (okA && okB) return Math.min(cA, cB);
    if (okA) return cA;
    if (okB) return cB;
    return Math.max(cA, cB);
  }

  /** A form's 'YYYY-MM-DD' as NOON on the company calendar (JSA-style day records). */
  function noonMs(dateStr, tz) {
    const m = YMD_RE.exec(String(dateStr || ''));
    if (!m) return NaN;
    return wallToMs(Number(m[1]), Number(m[2]), Number(m[3]), 12, 0, 0, tz === undefined ? zone() : tz);
  }

  /**
   * The instant to stamp on a record whose form says `dateStr`. Today (company
   * calendar) → now. A backdated day → that day at now's company wall-clock
   * time, so the stored instant reads back as the picked day in the company zone.
   */
  function stampMs(dateStr, nowMs, tz) {
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();
    const z = tz === undefined ? zone() : tz;
    const m = YMD_RE.exec(String(dateStr || ''));
    if (!m || dateStr === ymd(now, z)) return now;
    const p = parts(now, z);
    return wallToMs(Number(m[1]), Number(m[2]), Number(m[3]), p.hour, p.minute, p.second, z);
  }

  root.RecordDate = { zone, validZone, parts, ymd, today, addDays, wallToMs, noonMs, stampMs };
})(typeof window !== 'undefined' ? window : globalThis);
